import { supabase } from '../config';

const MAX_ATTEMPTS = 5;
const LOCK_WINDOW_SECONDS = 15 * 60;
const LOCAL_STORAGE_KEY = 'admesh_auth_login_guard_v1';

type AuthEventType = 'login_success' | 'login_failed' | 'login_blocked' | 'oauth_start';

interface LocalGuardEntry {
  failedAttempts: number;
  lockUntilEpoch: number;
  lastAttemptEpoch: number;
}

type LocalGuardStore = Record<string, LocalGuardEntry>;

export interface LoginGateStatus {
  allowed: boolean;
  waitSeconds: number;
  attemptsRemaining: number;
  source: 'local' | 'server' | 'combined';
}

const normalizeEmail = (email: string): string => email.trim().toLowerCase();

const getEmailKey = (email: string): string => encodeURIComponent(normalizeEmail(email));

const safeNowEpoch = (): number => Math.floor(Date.now() / 1000);

const readStore = (): LocalGuardStore => {
  try {
    const raw = localStorage.getItem(LOCAL_STORAGE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw) as LocalGuardStore;
    return parsed && typeof parsed === 'object' ? parsed : {};
  } catch {
    return {};
  }
};

const writeStore = (store: LocalGuardStore): void => {
  try {
    localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(store));
  } catch {
    // Ignore local storage failures.
  }
};

const getLocalGateStatus = (email: string): LoginGateStatus => {
  const key = getEmailKey(email);
  const store = readStore();
  const entry = store[key];
  if (!entry) {
    return {
      allowed: true,
      waitSeconds: 0,
      attemptsRemaining: MAX_ATTEMPTS,
      source: 'local',
    };
  }

  const now = safeNowEpoch();
  if (entry.lockUntilEpoch > now) {
    return {
      allowed: false,
      waitSeconds: entry.lockUntilEpoch - now,
      attemptsRemaining: 0,
      source: 'local',
    };
  }

  const stillInWindow = now - entry.lastAttemptEpoch <= LOCK_WINDOW_SECONDS;
  const activeFailures = stillInWindow ? entry.failedAttempts : 0;
  return {
    allowed: true,
    waitSeconds: 0,
    attemptsRemaining: Math.max(0, MAX_ATTEMPTS - activeFailures),
    source: 'local',
  };
};

const registerLocalFailure = (email: string): void => {
  const key = getEmailKey(email);
  const store = readStore();
  const now = safeNowEpoch();

  const current = store[key];
  const windowExpired = !current || now - current.lastAttemptEpoch > LOCK_WINDOW_SECONDS;
  const nextFailures = windowExpired ? 1 : current.failedAttempts + 1;

  store[key] = {
    failedAttempts: nextFailures,
    lastAttemptEpoch: now,
    lockUntilEpoch: nextFailures >= MAX_ATTEMPTS ? now + LOCK_WINDOW_SECONDS : 0,
  };

  writeStore(store);
};

const clearLocalFailures = (email: string): void => {
  const key = getEmailKey(email);
  const store = readStore();
  if (!store[key]) return;
  delete store[key];
  writeStore(store);
};

const logAuthEvent = async (
  eventType: AuthEventType,
  email?: string,
  userId?: string | null,
  metadata?: Record<string, unknown>
): Promise<void> => {
  try {
    await supabase.rpc('log_auth_event', {
      p_event_type: eventType,
      p_email: email ? normalizeEmail(email) : null,
      p_user_id: userId || null,
      p_metadata: metadata || {},
    });
  } catch (error) {
    // Logging should never block auth UX.
    console.warn('[AuthSecurity] Unable to log auth event.', error);
  }
};

const getServerGateStatus = async (email: string): Promise<LoginGateStatus | null> => {
  try {
    const { data, error } = await supabase.rpc('can_attempt_auth', {
      p_email: normalizeEmail(email),
    });

    if (error) {
      console.warn('[AuthSecurity] can_attempt_auth RPC failed.', error);
      return null;
    }

    const row = Array.isArray(data) ? data[0] : data;
    if (!row || typeof row !== 'object') {
      return null;
    }

    return {
      allowed: Boolean((row as any).allowed),
      waitSeconds: Number((row as any).wait_seconds || 0),
      attemptsRemaining: Number((row as any).attempts_remaining || 0),
      source: 'server',
    };
  } catch (error) {
    console.warn('[AuthSecurity] Unexpected can_attempt_auth failure.', error);
    return null;
  }
};

export const formatWaitTime = (seconds: number): string => {
  if (seconds <= 0) return 'a few seconds';
  if (seconds < 60) return `${seconds}s`;
  const minutes = Math.ceil(seconds / 60);
  return `${minutes} min`;
};

export const getSafeRedirectPath = (value?: string | null): string => {
  if (!value) return '/dashboard';
  const trimmed = value.trim();
  if (!trimmed.startsWith('/')) return '/dashboard';
  if (trimmed.startsWith('//')) return '/dashboard';
  if (trimmed.includes('://')) return '/dashboard';
  return trimmed;
};

export const getLoginGateStatus = async (email: string): Promise<LoginGateStatus> => {
  const normalized = normalizeEmail(email);
  const local = getLocalGateStatus(normalized);
  const server = await getServerGateStatus(normalized);

  if (!server) return local;

  return {
    allowed: local.allowed && server.allowed,
    waitSeconds: Math.max(local.waitSeconds, server.waitSeconds),
    attemptsRemaining: Math.min(local.attemptsRemaining, server.attemptsRemaining),
    source: 'combined',
  };
};

export const registerFailedLoginAttempt = async (
  email: string,
  metadata?: Record<string, unknown>
): Promise<void> => {
  registerLocalFailure(email);
  await logAuthEvent('login_failed', email, null, metadata);
};

export const registerSuccessfulLogin = async (
  email: string,
  userId?: string | null,
  metadata?: Record<string, unknown>
): Promise<void> => {
  clearLocalFailures(email);
  await logAuthEvent('login_success', email, userId || null, metadata);
};

export const registerBlockedLogin = async (
  email: string,
  metadata?: Record<string, unknown>
): Promise<void> => {
  await logAuthEvent('login_blocked', email, null, metadata);
};

export const registerOAuthStart = async (
  email?: string,
  metadata?: Record<string, unknown>
): Promise<void> => {
  await logAuthEvent('oauth_start', email, null, metadata);
};
